import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import DOMPurify from 'dompurify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Cropper from 'react-easy-crop';
import { 
  Menu, X, Shield, BookOpen, MapPin, TrendingDown, 
  Facebook, Instagram, Twitter, Calendar, Newspaper, ChevronRight,
  Mail, Phone, MapPinned, ExternalLink, Play, Quote, Award, User,
  Send, CheckCircle, Users, Download, Camera, ChevronLeft, Image, Building2,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, AlignLeft, AlignCenter, Link2,
  Crop, RotateCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scroll to top component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Helper to resolve image URLs (handles both uploaded and external images)
const resolveImageUrl = (url) => {
  if (!url) return null;
  
  // Handle full URLs from old uploads - extract just the path
  if (url.includes('preview.emergentagent.com') || url.includes('emergent.host')) {
    const match = url.match(/\/api\/uploads\/[^?#]+/);
    if (match) {
      return `${BACKEND_URL}${match[0]}`;
    }
  }
  
  // Handle /api/uploads/ paths (new format)
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  // Handle /uploads/ paths (old format) - convert to /api/uploads/
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return url;
};

// Icon mapping for dynamic icons from database
const ICON_MAP = {
  Shield: Shield,
  BookOpen: BookOpen,
  MapPin: MapPin,
  TrendingDown: TrendingDown,
  Award: Award,
  User: User,
  Users: Users,
  Calendar: Calendar,
  Building2: Building2
};

// Rich Text Editor Component using TipTap
const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({
        openOnClick: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
          title="Bold"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
          title="Italic"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
          title="Underline"
        >
          <UnderlineIcon size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
          title="Heading"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
          title="Subheading"
        >
          H3
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
          title="Bullet List"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
          title="Numbered List"
        >
          <ListOrdered size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
          title="Align Left"
        >
          <AlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
          title="Align Center"
        >
          <AlignCenter size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={setLink}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
          title="Add Link"
        >
          <Link2 size={18} />
        </button>
      </div>
      
      {/* Editor */}
      <EditorContent editor={editor} className="prose max-w-none p-4 min-h-[200px] focus:outline-none" />
    </div>
  );
};

// Helper to create cropped image
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = new window.Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

// Image Cropper Component
const ImageCropperModal = ({ imageSrc, onCropComplete, onCancel, aspectRatio = 1 }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedAspect, setSelectedAspect] = useState(aspectRatio);

  const onCropChange = useCallback((newCrop) => {
    setCrop(newCrop);
  }, [setCrop]);

  const onZoomChange = useCallback((newZoom) => {
    setZoom(newZoom);
  }, [setZoom]);

  const onCropCompleteCallback = useCallback((croppedArea, newCroppedAreaPixels) => {
    setCroppedAreaPixels(newCroppedAreaPixels);
  }, [setCroppedAreaPixels]);

  const handleSave = async () => {
    if (croppedAreaPixels) {
      const croppedBlob = await createCroppedImage(imageSrc, croppedAreaPixels);
      onCropComplete(croppedBlob);
    }
  };

  const aspectOptions = [
    { label: 'Square (1:1)', value: 1 },
    { label: 'Landscape (4:3)', value: 4/3 },
    { label: 'Portrait (3:4)', value: 3/4 },
    { label: 'Wide (16:9)', value: 16/9 },
    { label: 'Free', value: null },
  ];

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-black">
        <div className="flex items-center gap-4">
          <Crop className="text-white" size={24} />
          <span className="text-white font-heading text-lg uppercase">Crop Image</span>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedAspect || 'free'}
            onChange={(e) => setSelectedAspect(e.target.value === 'free' ? null : parseFloat(e.target.value))}
            className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600"
          >
            {aspectOptions.map((opt) => (
              <option key={opt.label} value={opt.value || 'free'}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Cropper */}
      <div className="flex-1 relative">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={selectedAspect}
          onCropChange={onCropChange}
          onCropComplete={onCropCompleteCallback}
          onZoomChange={onZoomChange}
        />
      </div>

      {/* Controls */}
      <div className="bg-black px-6 py-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-4 flex-1">
            <span className="text-white text-sm">Zoom:</span>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 max-w-xs"
            />
            <span className="text-white text-sm w-12">{Math.round(zoom * 100)}%</span>
          </div>
          <div className="flex gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-campaign-red text-white rounded hover:bg-campaign-red-hover transition-colors font-semibold"
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Page title helper
const usePageTitle = (title) => {
  useEffect(() => {
    const baseTitle = "Brandon J. Neider - Candidate for NYS Assembly";
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }, [title]);
};

// Campaign data
const CAMPAIGN_DATA = {
  donateUrl: "https://secure.winred.com/neider-for-assembly/donate",
  facebook: "https://www.facebook.com/profile.php?id=61585278703750",
  instagram: "https://www.instagram.com/neiderforassembly/",
  twitter: "https://x.com/BrandonNeider",
  youtube: null,
  logo: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/26uyl8fv_site%20logo%20RED%402x.png",
  logoWhite: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/yaogptfm_site%20logo%402x.png",
  logoRed: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/26uyl8fv_site%20logo%20RED%402x.png",
  images: {
    heroMain: "https://customer-assets-eiarnc6j.emergentagent.net/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/gppnnof7_IMG_6063.webp",
    heroMobile: "https://customer-assets-eiarnc6j.emergentagent.net/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/gppnnof7_IMG_6063.webp",
    brandonAndMike: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/rxjp7gmk_splash.png",
    campaignTrail: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/rxjp7gmk_splash.png",
    meetingVoters: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/sswm4s9g_497465529_10233075666210990_9087778353499454750_n.jpg",
  },
  gallery: [
    {
      url: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/sswm4s9g_497465529_10233075666210990_9087778353499454750_n.jpg",
      caption: "Brandon Neider speaking at community event"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_18e014fb-6a0a-41b1-a682-d9ff46372c3d/artifacts/rxjp7gmk_splash.png",
      caption: "On the campaign trail"
    },
  ],
  tagline: "Lifelong Yonkers Resident. Community Leader. Union Chair.",
  slogan: "Bringing Real Representation to District 89",
  subSlogan: "Decades Of Neglect End When Real Representation Begins.",
  endSlogan: "No More BS In Albany.",
  bio: `Brandon J. Neider is a lifelong resident of Yonkers, NY. He is a proud alumnus of Yonkers Public Schools, notably Lincoln High School.

Brandon began his civic engagement at age 18, becoming one of the youngest candidates ever to run for public office in Yonkers. He ran for City Council as an independent in 2013.`,
  bioShort: `Since then, Neider has led a successful career in IT. He continued his deep commitment to the community, having served as President of the East Yonkers Kiwanis, Chair of the City of Yonkers Youth Advisory Board, and former board member for his neighborhood Lincoln Park Taxpayer's Association and the 2nd Precinct Community Council.`,
  bioExtended: `He is attributed to numerous charitable and philanthropic endeavors including organizing food drives during the COVID-19 Pandemic, Holiday Toy Drives and engaging in local advocacy. Professionally, Neider has enjoyed a long-term career with the Yonkers Public Libraries. In addition to his public service employment, he is the owner and founder of Yonkers Auto Detailers and serves as Board Chair for SEIU 704, representing City of Yonkers employees.`,
  contact: {
    website: "neiderforassembly.com",
    email: "contact@neiderforassembly.com",
    address: "P.O. Box 1033 - Yonkers, NY 10704",
    paidFor: "Brandon J. Neider"
  },
  donateAmounts: [5, 25, 50, 100],
  youtubeVideo: "https://www.youtube.com/embed/wz9CdHD5bMM",
  youtubeThumb: "https://test.brandonneider.com/wp-content/uploads/2026/02/628454234_122112569049175956_2248950071570687522_n-320x180.jpg",
  upcomingEvent: {
    title: "St. Patrick's Day Celebration",
    date: "TBD",
    venue: "The Heritage",
    address: "960 McLean Ave, Yonkers, NY 10704",
    description: "Join us to celebrate St. Patrick's Day, enjoy some of the finest food on the Emerald Mile, and help Save New York!"
  },
  issues: [
    { 
      title: "End Unfair Taxes & Fees", 
      icon: TrendingDown, 
      desc: "Repeal the Yonkers income tax, eliminate predatory parking/traffic fines, and stop using tolls and tickets as hidden taxes on working families.",
      details: [
        "End the Yonkers Income Tax",
        "Promote Ownership with Condo/Co-Ops",
        "Regulate Sports Betting",
        "Stop hidden taxes on drivers"
      ]
    },
    { 
      title: "Restore Public Safety", 
      icon: Shield, 
      desc: "Fully repeal bail reform to keep dangerous offenders off the streets and restore judicial discretion to protect our communities.",
      details: [
        "Repeal Bail Reform",
        "Restore Judicial Discretion",
        "Protect Families & Communities",
        "End catch-and-release policies"
      ]
    },
    { 
      title: "Fix Crumbling Infrastructure", 
      icon: MapPin, 
      desc: "Remove the I-87 Yonkers toll, properly maintain state roads, reopen Indian Point, and end forced 'opt-in' energy programs.",
      details: [
        "Remove I-87 Yonkers Toll",
        "Repave State Roads in Yonkers & Mt Vernon",
        "Reopen Indian Point",
        "End Mandatory Energy Scams"
      ]
    },
    { 
      title: "Schools & Trades", 
      icon: BookOpen, 
      desc: "Close the unfair school funding gap and expand trade programs so young people can earn strong wages without crushing student debt.",
      details: [
        "Fair School Budgets",
        "Expand Trade Programs",
        "Plumbing, Electrical, HVAC Training",
        "SUNY Vocational Pathways"
      ]
    },
  ]
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav data-testid="navbar" className={`${scrolled ? 'fixed top-0 left-0 right-0 shadow-lg' : 'relative'} z-50 transition-all duration-300 bg-black/75 backdrop-blur-sm`}>
      <div className="container-campaign">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src={CAMPAIGN_DATA.logoWhite} 
              alt="Neider for State Assembly" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10">
            <Link to="/" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-home">Home</Link>
            <Link to="/about" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-about">About</Link>
            <Link to="/events" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-events">Events</Link>
            <Link to="/news" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-news">News</Link>
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-donate text-lg"
              data-testid="nav-donate-btn"
            >
              Donate
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mobile-menu-enter pb-4" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Home</Link>
              <Link to="/about" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>About</Link>
              <Link to="/events" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Events</Link>
              <Link to="/news" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>News</Link>
              <a 
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-donate text-center"
              >
                Donate
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-black text-white py-16">
      <div className="container-campaign">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Tagline */}
          <div className="col-span-1 md:col-span-2">
            <img 
              src={CAMPAIGN_DATA.logoWhite} 
              alt="Neider for State Assembly" 
              className="h-20 w-auto mb-6"
            />
            <p className="text-gray-300 mb-2 font-black">
              {CAMPAIGN_DATA.slogan}
            </p>
            <p className="text-gray-400 mb-4 font-body">
              {CAMPAIGN_DATA.tagline}
            </p>
            <div className="flex space-x-4">
              <a href={CAMPAIGN_DATA.twitter} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-twitter">
                <Twitter size={20} />
              </a>
              <a href={CAMPAIGN_DATA.facebook} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-facebook">
                <Facebook size={20} />
              </a>
              <a href={CAMPAIGN_DATA.instagram} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-instagram">
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors font-semibold">Home</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors font-semibold">About Brandon</Link></li>
              <li><Link to="/events" className="text-gray-300 hover:text-white transition-colors font-semibold">Events</Link></li>
              <li><Link to="/news" className="text-gray-300 hover:text-white transition-colors font-semibold">In The News</Link></li>
              <li><Link to="/privacy" className="text-gray-300 hover:text-white transition-colors font-semibold">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors font-semibold">Terms of Service</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Contact</h4>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-campaign-red" />
                <a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="hover:text-white transition-colors">
                  {CAMPAIGN_DATA.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPinned size={16} className="text-campaign-red" />
                <span>{CAMPAIGN_DATA.contact.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <ExternalLink size={16} className="text-campaign-red" />
                <a href={`https://${CAMPAIGN_DATA.contact.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  {CAMPAIGN_DATA.contact.website}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p className="font-semibold">Paid for by Neider for Assembly</p>
          <p className="mt-2">&copy; 2026 Brandon J. Neider</p>
          <p className="mt-4">
            Powered by <a href="https://newlogical.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-campaign-red transition-colors">New Logical</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-[600px] lg:min-h-[calc(100vh-5rem)] overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${CAMPAIGN_DATA.images.heroMain})` }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Content Container */}
      <div className="relative z-10 min-h-[600px] lg:min-h-[calc(100vh-5rem)] flex items-center">
        <div className="container-campaign w-full">
          <div className="max-w-3xl">
            {/* Main headline */}
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white uppercase mb-4 leading-tight">
              Bringing Real Representation to District 89
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl lg:text-2xl text-white/90 font-body mb-8">
              {CAMPAIGN_DATA.subSlogan}
            </p>
            
            {/* CTA Button */}
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-campaign-red text-white px-10 py-5 text-xl font-heading uppercase tracking-wide hover:bg-campaign-red-hover transition-colors"
              data-testid="hero-donate-btn"
            >
              Donate
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Meet Brandon Section
const MeetBrandonSection = () => {
  return (
    <section data-testid="meet-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-campaign-red font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
            <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-6" data-testid="meet-title">
              Meet Brandon Neider
            </h2>
            <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bio}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bioShort}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-8">
              {CAMPAIGN_DATA.bioExtended}
            </p>
            <Link to="/about" className="btn-secondary inline-flex items-center gap-2 font-black">
              Read Full Bio <ChevronRight size={20} />
            </Link>
          </div>
          <div className="order-1 lg:order-2">
            <img 
              src={CAMPAIGN_DATA.images.brandonAndMike}
              alt="Brandon Neider meeting with community members" 
              className="w-full h-auto rounded-sm shadow-2xl"
              data-testid="meet-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Issues Section
const IssuesSection = ({ issues }) => {
  // Use provided issues from API or fall back to static data
  const displayIssues = issues && issues.length > 0 ? issues : CAMPAIGN_DATA.issues;
  
  return (
    <section data-testid="issues-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="issues-title">
            Issues That Actually Matter
          </h2>
          <p className="text-lg text-gray-600 font-body max-w-2xl mx-auto">
            {CAMPAIGN_DATA.subSlogan}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {displayIssues.map((issue, index) => {
            // Handle both static icons (components) and dynamic icons (strings)
            const IconComponent = typeof issue.icon === 'string' ? (ICON_MAP[issue.icon] || Shield) : issue.icon;
            return (
              <div 
                key={issue.id || index} 
                className="bg-white p-8 rounded-sm shadow-sm card-hover text-center"
                data-testid={`issue-card-${index}`}
              >
                <div className="issue-icon">
                  <IconComponent className="w-8 h-8 text-black" />
                </div>
                <h3 className="font-heading text-xl uppercase text-black mb-3">{issue.title}</h3>
                <p className="text-gray-600 font-body text-sm">{issue.description || issue.desc}</p>
              </div>
            );
          })}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-2xl font-heading uppercase text-campaign-red">{CAMPAIGN_DATA.endSlogan}</p>
          <a 
            href={CAMPAIGN_DATA.donateUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mt-6 bg-campaign-red text-white px-10 py-4 text-lg font-heading uppercase tracking-wide hover:bg-campaign-red-hover transition-colors"
          >
            Donate
          </a>
        </div>
      </div>
    </section>
  );
};

// Video Section - Commented out for now (no video available)
// const VideoSection = () => {
//   if (!CAMPAIGN_DATA.youtube) return null;
//   return (
//     <section data-testid="video-section" className="py-20 bg-black">
//       <div className="container-campaign">
//         <div className="text-center mb-12">
//           <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-4" data-testid="video-title">
//             Campaign Announcement
//           </h2>
//         </div>
//         <div className="max-w-4xl mx-auto">
//           <div className="video-container rounded-sm overflow-hidden shadow-2xl">
//             <iframe 
//               src={CAMPAIGN_DATA.youtube}
//               title="Brandon Neider Campaign"
//               frameBorder="0"
//               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
//               allowFullScreen
//               data-testid="youtube-video"
//             />
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// Photo Gallery Section
const GallerySection = ({ photos }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showSlideshow, setShowSlideshow] = useState(false);
  
  // Use provided photos or fall back to static gallery
  const galleryPhotos = photos && photos.length > 0 ? photos : CAMPAIGN_DATA.gallery;
  
  // Show only first 18 photos (6 rows x 3 columns) in the grid
  const maxVisible = 18;
  const visiblePhotos = galleryPhotos.slice(0, maxVisible);
  const hasMore = galleryPhotos.length > maxVisible;
  
  const openSlideshow = (index) => {
    setSelectedIndex(index);
    setShowSlideshow(true);
  };
  
  const closeSlideshow = useCallback(() => {
    setShowSlideshow(false);
    setSelectedIndex(null);
  }, []);
  
  const goToPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? galleryPhotos.length - 1 : prev - 1));
  }, [galleryPhotos.length]);
  
  const goToNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setSelectedIndex((prev) => (prev === galleryPhotos.length - 1 ? 0 : prev + 1));
  }, [galleryPhotos.length]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSlideshow) return;
      if (e.key === 'ArrowLeft') goToPrev(e);
      if (e.key === 'ArrowRight') goToNext(e);
      if (e.key === 'Escape') closeSlideshow();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSlideshow, goToPrev, goToNext, closeSlideshow]);

  return (
    <section data-testid="gallery-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="gallery-title">
            On The Campaign Trail
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Brandon Neider meeting with voters and community leaders across District 89
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {visiblePhotos.map((photo, index) => (
            <div 
              key={photo.id || index}
              className="relative aspect-square overflow-hidden rounded-sm cursor-pointer group bg-gray-200"
              onClick={() => openSlideshow(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={resolveImageUrl(photo.url)} 
                alt={photo.caption || 'Campaign photo'}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                  const placeholder = document.createElement('div');
                  placeholder.className = 'text-gray-400 text-center p-4';
                  // Create SVG element safely without innerHTML
                  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                  svg.setAttribute('class', 'w-12 h-12 mx-auto mb-2');
                  svg.setAttribute('fill', 'none');
                  svg.setAttribute('stroke', 'currentColor');
                  svg.setAttribute('viewBox', '0 0 24 24');
                  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                  path.setAttribute('stroke-linecap', 'round');
                  path.setAttribute('stroke-linejoin', 'round');
                  path.setAttribute('stroke-width', '2');
                  path.setAttribute('d', 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z');
                  svg.appendChild(path);
                  const span = document.createElement('span');
                  span.className = 'text-xs';
                  span.textContent = 'Image unavailable';
                  placeholder.appendChild(svg);
                  placeholder.appendChild(span);
                  e.target.parentElement.appendChild(placeholder);
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          ))}
        </div>
        
        {/* See More Button */}
        {hasMore && (
          <div className="text-center mt-8">
            <button
              onClick={() => openSlideshow(0)}
              className="bg-black text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-gray-800 transition-colors inline-flex items-center gap-2"
              data-testid="gallery-see-more"
            >
              <Camera size={20} />
              See All {galleryPhotos.length} Photos
            </button>
          </div>
        )}
      </div>
      
      {/* Full-screen Slideshow */}
      {showSlideshow && selectedIndex !== null && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onClick={closeSlideshow}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-black/50">
            <span className="text-white font-heading">
              {selectedIndex + 1} / {galleryPhotos.length}
            </span>
            <button 
              className="text-white hover:text-gray-300 transition-colors"
              onClick={closeSlideshow}
              data-testid="slideshow-close"
            >
              <X size={32} />
            </button>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center relative px-4" onClick={(e) => e.stopPropagation()}>
            {/* Previous Button */}
            <button
              onClick={goToPrev}
              className="absolute left-4 md:left-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-prev"
            >
              <ChevronLeft size={32} />
            </button>
            
            {/* Image */}
            <div className="max-w-5xl max-h-[75vh] flex items-center justify-center">
              <img 
                src={resolveImageUrl(galleryPhotos[selectedIndex].url)} 
                alt={galleryPhotos[selectedIndex].caption || 'Campaign photo'}
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
            
            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-next"
            >
              <ChevronRight size={32} />
            </button>
          </div>
          
          {/* Caption */}
          <div className="px-6 py-4 bg-black/50 text-center">
            <p className="text-white font-body">
              {galleryPhotos[selectedIndex].caption || 'On the campaign trail'}
            </p>
          </div>
          
          {/* Thumbnail Strip */}
          <div className="bg-black/80 px-4 py-3 overflow-x-auto">
            <div className="flex gap-2 justify-center min-w-max">
              {galleryPhotos.map((photo, index) => (
                <button
                  key={photo.id || index}
                  onClick={(e) => { e.stopPropagation(); setSelectedIndex(index); }}
                  className={`w-16 h-16 rounded overflow-hidden flex-shrink-0 transition-all ${
                    index === selectedIndex ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  <img 
                    src={resolveImageUrl(photo.url)} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Events Section
const EventsSection = ({ events, showAll = false }) => {
  const displayEvents = showAll ? events : events.slice(0, 3);
  
  return (
    <section data-testid="events-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="events-title">
            Upcoming Events
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Join us on the campaign trail
          </p>
        </div>
        
        {displayEvents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-sm">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No upcoming events at this time. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayEvents.map((event, index) => (
              <div 
                key={event.id || index} 
                className="bg-gray-50 rounded-sm overflow-hidden card-hover"
                data-testid={`event-card-${index}`}
              >
                <div className="p-6">
                  <div className="flex items-center gap-2 text-campaign-red font-semibold mb-2">
                    <Calendar size={18} />
                    <span>{event.date}</span>
                    {event.time && <span>| {event.time}</span>}
                  </div>
                  <h3 className="font-heading text-xl text-black uppercase mb-2">{event.title}</h3>
                  <p className="text-gray-600 font-body mb-4">{event.description}</p>
                  {event.location && (
                    <p className="text-gray-500 text-sm mb-4 flex items-center gap-1">
                      <MapPin size={14} /> {event.location}
                    </p>
                  )}
                  <a 
                    href={event.rsvp_link || "#"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-black text-white font-bold uppercase tracking-wider py-2 px-6 text-sm hover:bg-black-light transition-colors"
                  >
                    RSVP
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!showAll && events.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/events" className="btn-secondary inline-flex items-center gap-2">
              View All Events <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// News Section
const NewsSection = ({ news, showAll = false }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const displayNews = showAll ? news : news.slice(0, 3);
  
  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };
  
  return (
    <section data-testid="news-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="news-title">
            In The News
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Latest coverage and updates from the campaign
          </p>
        </div>
        
        {displayNews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-sm">
            <Newspaper className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No news articles yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayNews.map((article, index) => {
              const isOriginal = article.content_type === 'original';
              const videoId = getYouTubeId(article.video_url);
              
              return (
                <div 
                  key={article.id || index} 
                  className="bg-white rounded-sm overflow-hidden shadow-sm card-hover"
                  data-testid={`news-card-${index}`}
                >
                  {/* Video thumbnail or image */}
                  {videoId ? (
                    <div 
                      className="relative w-full h-48 bg-black cursor-pointer group"
                      onClick={() => setSelectedArticle(article)}
                    >
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
                        alt={article.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                        <div className="w-16 h-16 bg-campaign-red rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-red text-white text-xs px-2 py-1 font-semibold uppercase">
                          Video
                        </span>
                      )}
                    </div>
                  ) : article.image_url ? (
                    <div className="relative">
                      <img src={resolveImageUrl(article.image_url)} alt={article.title} className="w-full h-48 object-cover" />
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-red text-white text-xs px-2 py-1 font-semibold uppercase">
                          Campaign Update
                        </span>
                      )}
                    </div>
                  ) : isOriginal && (
                    <div className="w-full h-48 bg-black flex items-center justify-center">
                      <span className="text-white font-heading text-2xl uppercase">Campaign Update</span>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="text-sm text-gray-500 mb-2">
                      {article.published_date} {!isOriginal && article.source && `| ${article.source}`}
                    </div>
                    <h3 className="font-heading text-xl text-black uppercase mb-2">{article.title}</h3>
                    <p className="text-gray-600 font-body mb-4">{article.summary}</p>
                    
                    {isOriginal ? (
                      <button 
                        onClick={() => setSelectedArticle(article)}
                        className="text-campaign-red font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        {videoId ? 'Watch Video' : 'Read More'} <ChevronRight size={14} />
                      </button>
                    ) : article.link && (
                      <a 
                        href={article.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-campaign-red font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        Read More <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!showAll && news.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/news" className="btn-secondary inline-flex items-center gap-2">
              View All News <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
      
      {/* Article Modal for Original Content */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedArticle(null)}
        >
          <div 
            className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <span className="text-sm text-gray-500">{selectedArticle.published_date}</span>
              <button 
                onClick={() => setSelectedArticle(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Video embed */}
            {getYouTubeId(selectedArticle.video_url) && (
              <div className="aspect-video w-full bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(selectedArticle.video_url)}?autoplay=1`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedArticle.title}
                />
              </div>
            )}
            
            {/* Content */}
            <div className="p-6 md:p-10">
              <h2 className="font-heading text-3xl md:text-4xl text-black uppercase mb-4">
                {selectedArticle.title}
              </h2>
              
              {selectedArticle.image_url && !getYouTubeId(selectedArticle.video_url) && (
                <img 
                  src={resolveImageUrl(selectedArticle.image_url)} 
                  alt={selectedArticle.title}
                  className="w-full max-h-96 object-cover rounded mb-6"
                />
              )}
              
              <div className="prose prose-lg max-w-none">
                {selectedArticle.content ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.content) }} />
                ) : (
                  <p className="text-gray-600 font-body text-lg">{selectedArticle.summary}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Quick Donate Section (appears early on homepage)
const QuickDonateSection = () => {
  return (
    <section data-testid="quick-donate" className="py-12 bg-black">
      <div className="container-campaign">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl text-white uppercase mb-2">
              Support Brandon Today
            </h3>
            <p className="text-gray-400">Every dollar helps bring real representation to District 89</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {CAMPAIGN_DATA.donateAmounts.map((amount) => (
              <a 
                key={amount}
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-campaign-red hover:bg-campaign-red-hover text-white font-bold py-3 px-6 text-lg transition-colors"
              >
                ${amount}
              </a>
            ))}
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-bold py-3 px-6 text-lg transition-colors"
            >
              Other
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => {
  if (!CAMPAIGN_DATA.youtubeVideo) return null;
  
  return (
    <section data-testid="video-section" className="py-20 bg-gray-900">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-4">
            Watch Brandon's Message
          </h2>
          <p className="text-lg text-gray-400 font-body">
            Hear directly from Brandon about his vision for District 89
          </p>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="video-container rounded-sm overflow-hidden shadow-2xl">
            <iframe 
              src={CAMPAIGN_DATA.youtubeVideo}
              title="Brandon Neider Campaign Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-video"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Donate CTA Section
const DonateCTA = () => {
  return (
    <section data-testid="donate-cta" className="py-20 bg-campaign-red">
      <div className="container-campaign text-center">
        <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-6">
          Support the Campaign
        </h2>
        <p className="text-xl text-white/90 font-body mb-8 max-w-2xl mx-auto">
          Help Brandon Neider bring real representation to District 89. Every contribution makes a difference.
        </p>
        
        {/* Quick donate amounts */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          {CAMPAIGN_DATA.donateAmounts.map((amount) => (
            <a 
              key={amount}
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-campaign-red hover:bg-gray-100 font-bold py-3 px-8 text-lg transition-colors"
            >
              ${amount}
            </a>
          ))}
        </div>
        
        <a 
          href={CAMPAIGN_DATA.donateUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block bg-black text-white font-bold uppercase tracking-wider py-4 px-12 text-lg hover:bg-gray-900 transition-colors shadow-lg"
          data-testid="donate-cta-btn"
        >
          Donate Any Amount
        </a>
      </div>
    </section>
  );
};

// Endorsements Section
const EndorsementsSection = ({ endorsements }) => {
  if (!endorsements || endorsements.length === 0) {
    return null;
  }
  
  const orgEndorsements = endorsements.filter(e => e.endorsement_type === 'organization');
  const individualEndorsements = endorsements.filter(e => e.endorsement_type !== 'organization');
  
  return (
    <section data-testid="endorsements-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="endorsements-title">
            Endorsements
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Leaders and organizations who support Brandon Neider for Assembly
          </p>
        </div>
        
        {/* Organization Endorsements */}
        {orgEndorsements.length > 0 && (
          <div className="mb-16">
            <h3 className="font-heading text-2xl text-black uppercase mb-8 text-center">Endorsed By</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {orgEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover flex flex-col items-center text-center"
                  data-testid={`org-endorsement-${index}`}
                >
                  {endorsement.image_url ? (
                    <img 
                      src={resolveImageUrl(endorsement.image_url)} 
                      alt={endorsement.name}
                      className="h-20 w-auto object-contain mb-4"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded bg-black/10 flex items-center justify-center mb-4">
                      <Building2 className="w-10 h-10 text-black" />
                    </div>
                  )}
                  <h4 className="font-heading text-lg text-black uppercase">{endorsement.name}</h4>
                  {endorsement.title && (
                    <p className="text-gray-500 text-sm mt-1">{endorsement.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Individual Endorsements */}
        {individualEndorsements.length > 0 && (
          <div>
            {orgEndorsements.length > 0 && (
              <h3 className="font-heading text-2xl text-black uppercase mb-8 text-center">Individual Endorsements</h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {individualEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover"
                  data-testid={`endorsement-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    {endorsement.image_url ? (
                      <img 
                        src={resolveImageUrl(endorsement.image_url)} 
                        alt={endorsement.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-black/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-8 h-8 text-black" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-heading text-lg text-black uppercase">{endorsement.name}</h3>
                      {endorsement.title && (
                        <p className="text-campaign-red font-semibold text-sm">{endorsement.title}</p>
                      )}
                      {endorsement.organization && (
                        <p className="text-gray-500 text-sm">{endorsement.organization}</p>
                      )}
                    </div>
                  </div>
                  {endorsement.quote && (
                    <div className="mt-4 relative">
                      <Quote className="w-6 h-6 text-black/20 absolute -top-1 -left-1" />
                      <p className="text-gray-600 font-body italic pl-5">"{endorsement.quote}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Email/Text Signup Section
// Signup Section with Brevo Form
const SignupSection = () => {
  return (
    <section data-testid="signup-section" className="py-20 bg-black">
      <div className="container-campaign">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
            Stay Connected
          </h2>
          <p className="text-lg text-gray-300 font-body mb-8">
            Join our email list to receive campaign updates, event invitations, and ways to get involved.
          </p>
          
          <div className="bg-white rounded-sm overflow-hidden shadow-lg">
            <iframe 
              width="100%" 
              height="680" 
              src="https://21c8a904.sibforms.com/serve/MUIFAEjm91F3cRUmw-IgteWfH7P6pBotwy_CGJxGw-qMk8quHXHkUQs-amvF_bAyySosen3Aj4kdHIJy5QegwTjTEx-oGpLMZeHXaZkzrAo4YnrJwmJrC5BsUgYP4Mm5qemrHIHc7jFEB9Vzv5LFTj8isu0_UCv7WLNSem9ENuW78Y7O0dECkvsgOUGCD2gOraZ9ml2QJr9Y4t_s" 
              frameBorder="0" 
              scrolling="auto" 
              allowFullScreen 
              style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', maxWidth: '100%' }}
              title="Newsletter Signup"
            />
          </div>
          
          {/* SMS Consent Disclaimer - Required for 10DLC Compliance */}
          <div className="mt-6 text-left bg-gray-900 p-4 rounded-sm">
            <p className="text-gray-300 text-sm leading-relaxed">
              By submitting this form and checking the SMS opt-in box, you consent to receive text messages. 
              Messages may include campaign updates, event information, volunteer opportunities, and requests for donation. 
              Msg & data rates may apply. Msg frequency may vary. Reply STOP to opt-out and HELP for help. 
              View our <Link to="/terms" className="text-campaign-red hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-campaign-red hover:underline">Privacy Policy</Link> for more information.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Home Page
const Home = () => {
  usePageTitle(null); // Use base title for home
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, newsRes, endorsementsRes, galleryRes, issuesRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/news`),
          axios.get(`${API}/endorsements`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/issues`)
        ]);
        setEvents(eventsRes.data);
        setNews(newsRes.data);
        setEndorsements(endorsementsRes.data);
        setGalleryPhotos(galleryRes.data);
        setIssues(issuesRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
        // Set placeholder data
        setEvents([{
          id: 'placeholder',
          title: 'Campaign Fundraiser',
          description: 'Join us for an evening reception to support John Isaac for Assembly.',
          date: 'TBD',
          time: 'TBD',
          location: 'Yonkers, NY',
          rsvp_link: '#'
        }]);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <QuickDonateSection />
      <MeetBrandonSection />
      <IssuesSection issues={issues} />
      <VideoSection />
      <GallerySection photos={galleryPhotos} />
      <EndorsementsSection endorsements={endorsements} />
      <SignupSection />
      <EventsSection events={events} />
      <NewsSection news={news} />
      <DonateCTA />
    </div>
  );
};

// About Page
const AboutPage = () => {
  usePageTitle("About Brandon");
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-black">
        <div className="container-campaign">
          <p className="text-campaign-red font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">About Brandon</h1>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-20">
        <div className="container-campaign">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl text-black uppercase mb-6">Meet Brandon J. Neider</h2>
              <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bio}
              </p>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bioShort}
              </p>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bioExtended}
              </p>
              
              {/* Real Representation */}
              <div className="bg-gray-50 p-8 rounded-sm mb-8">
                <h3 className="font-heading text-2xl text-black uppercase mb-4">Real Representation for District 89</h3>
                <p className="text-gray-700 mb-4">Decades of neglect end when real representation begins. District 89 deserves:</p>
                <ul className="space-y-2 mb-4 text-gray-700">
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Lower taxes and fewer hidden fees</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Safe streets and communities</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Working infrastructure</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-red">•</span> Fair treatment for workers and students</li>
                </ul>
                <p className="text-gray-700 font-semibold">It's time for change in Albany.</p>
              </div>

              <h3 className="font-heading text-2xl text-black uppercase mb-4">The Issues</h3>
              
              {/* End Unfair Taxes */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">End Unfair Taxes & Fees</strong>
                    <p className="text-gray-600 mt-1">Working families are being nickel-and-dimed by Albany. It's time to fight back.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• End the Yonkers Income Tax</li>
                      <li>• Eliminate predatory parking/traffic fines</li>
                      <li>• End PILOT's & Tax Breaks for Rentals</li>
                      <li>• Regulate Sports Betting to protect families</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Restore Public Safety */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <Shield className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">Restore Public Safety</strong>
                    <p className="text-gray-600 mt-1">New York's failed bail reform laws have allowed too many criminals to be released without bail, endangering families and communities.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Fully repeal Bail Reform</li>
                      <li>• Restore judicial discretion</li>
                      <li>• Support 2nd Amendment rights</li>
                      <li>• Repeal the Concealed Carry Improvement Act</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Fix Infrastructure */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">Fix Crumbling Infrastructure</strong>
                    <p className="text-gray-600 mt-1">Our roads are falling apart and energy costs are out of control.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Remove I-87 Yonkers Toll</li>
                      <li>• Repave and maintain state roads</li>
                      <li>• Reopen Indian Point for affordable energy</li>
                      <li>• End mandatory "opt-in" energy scams</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Workers & Schools */}
              <div className="mb-8">
                <div className="flex items-start gap-3 mb-2">
                  <BookOpen className="w-6 h-6 text-campaign-red mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">Workers, Schools & Opportunity</strong>
                    <p className="text-gray-600 mt-1">Public workers deserve fair treatment and our students deserve better opportunities.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Fix Tier 6 pensions</li>
                      <li>• Repeal or reform the Taylor Law</li>
                      <li>• Close school funding gaps</li>
                      <li>• Expand trade and vocational education</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* No More BS */}
              <div className="bg-black text-white p-8 rounded-sm mb-8">
                <h3 className="font-heading text-2xl uppercase mb-4">No More BS In Albany.</h3>
                <p className="text-gray-200 leading-relaxed">
                  Brandon has served his community his entire life. As President of the East Yonkers Kiwanis, Chair of the City of Yonkers Youth Advisory Board, and Board Chair for SEIU 704, he knows what it takes to fight for working people. It's time to bring that fight to Albany.
                </p>
              </div>
              
              {/* CTA */}
              <div className="bg-campaign-red text-white p-8 rounded-sm">
                <h3 className="font-heading text-2xl uppercase mb-4">Join the Movement</h3>
                <p className="text-white/90 mb-4">This campaign is about:</p>
                <ul className="space-y-2 text-white/90 mb-4">
                  <li className="flex items-center gap-2">✓ Real representation for District 89</li>
                  <li className="flex items-center gap-2">✓ Standing up for working families</li>
                  <li className="flex items-center gap-2">✓ Ending decades of neglect</li>
                  <li className="flex items-center gap-2">✓ Common sense leadership</li>
                </ul>
                <a 
                  href={CAMPAIGN_DATA.donateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-campaign-red px-8 py-3 font-heading uppercase tracking-wide hover:bg-gray-100 transition-colors"
                >
                  Donate Now
                </a>
              </div>
            </div>
            
            <div>
              <img 
                src={CAMPAIGN_DATA.images.heroMobile}
                alt="Brandon Neider" 
                className="w-full rounded-sm shadow-lg mb-6"
              />
              <img 
                src={CAMPAIGN_DATA.images.meetingVoters}
                alt="Brandon Neider meeting with voters" 
                className="w-full rounded-sm shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>
      
      <DonateCTA />
    </div>
  );
};

// Events Page
const EventsPage = () => {
  usePageTitle("Events");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (e) {
        console.error("Error fetching events:", e);
        setEvents([{
          id: 'placeholder',
          title: 'Campaign Fundraiser',
          description: 'Join us for an evening reception to support John Isaac for Assembly.',
          date: 'TBD',
          time: 'TBD',
          location: 'Yonkers, NY',
          rsvp_link: '#'
        }]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="pt-28" data-testid="events-page">
      <section className="py-20 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">Events</h1>
        </div>
      </section>
      
      <EventsSection events={events} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// News Page
const NewsPage = () => {
  usePageTitle("In The News");
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`${API}/news`);
        setNews(res.data);
      } catch (e) {
        console.error("Error fetching news:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="pt-28" data-testid="news-page">
      <section className="py-20 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">In The News</h1>
        </div>
      </section>
      
      <NewsSection news={news} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// Admin Login Page
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        auth: { username, password }
      });
      onLogin({ username, password });
    } catch (e) {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="admin-login">
      <div className="bg-white p-8 rounded-sm shadow-lg w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CAMPAIGN_DATA.logoSvg} alt="Logo" className="h-20 mx-auto mb-4 logo-light" />
          <h1 className="font-heading text-2xl text-black uppercase">Admin Login</h1>
        </div>
        
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-gray-700 font-black mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              required
              data-testid="admin-username"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 font-black mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              required
              data-testid="admin-password"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white font-black uppercase py-3 hover:bg-black-light transition-colors disabled:opacity-50"
            data-testid="admin-submit"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <Link to="/" className="text-gray-500 hover:text-black font-semibold">
            &larr; Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ auth, onLogout }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [issues, setIssues] = useState([]);
  const [galleryDisplayMode, setGalleryDisplayMode] = useState('manual');
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const authConfig = { auth: { username: auth.username, password: auth.password } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, newsRes, endorsementsRes, subscribersRes, galleryRes, issuesRes, gallerySettingRes] = await Promise.all([
        axios.get(`${API}/admin/events`, authConfig),
        axios.get(`${API}/admin/news`, authConfig),
        axios.get(`${API}/admin/endorsements`, authConfig),
        axios.get(`${API}/admin/subscribers`, authConfig),
        axios.get(`${API}/admin/gallery`, authConfig),
        axios.get(`${API}/admin/issues`, authConfig),
        axios.get(`${API}/admin/settings/gallery_display`, authConfig)
      ]);
      setEvents(eventsRes.data);
      setNews(newsRes.data);
      setEndorsements(endorsementsRes.data);
      setSubscribers(subscribersRes.data);
      setGallery(galleryRes.data);
      setIssues(issuesRes.data);
      setGalleryDisplayMode(gallerySettingRes.data.value || 'manual');
    } catch (e) {
      console.error("Error fetching admin data:", e);
    } finally {
      setLoading(false);
    }
  };

  const updateGalleryDisplayMode = async (mode) => {
    try {
      await axios.put(`${API}/admin/settings/gallery_display`, { value: mode }, authConfig);
      setGalleryDisplayMode(mode);
    } catch (e) {
      console.error("Error updating gallery display mode:", e);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, authConfig);
      fetchData();
    } catch (e) {
      console.error("Error deleting:", e);
    }
  };

  const handleSave = async (type, data) => {
    try {
      if (editItem) {
        await axios.put(`${API}/admin/${type}/${editItem.id}`, data, authConfig);
      } else {
        await axios.post(`${API}/admin/${type}`, data, authConfig);
      }
      fetchData();
      setShowForm(false);
      setEditItem(null);
    } catch (e) {
      console.error("Error saving:", e);
    }
  };

  const exportSubscribers = () => {
    const csv = [
      ['Email', 'First Name', 'Last Name', 'Phone', 'Wants Email', 'Wants Text', 'Subscribed At'].join(','),
      ...subscribers.map(s => [
        s.email,
        s.first_name || '',
        s.last_name || '',
        s.phone || '',
        s.wants_email ? 'Yes' : 'No',
        s.wants_text ? 'Yes' : 'No',
        s.subscribed_at
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-black text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img src={CAMPAIGN_DATA.logoSvg} alt="Logo" className="h-12" />
          <h1 className="font-heading text-xl uppercase">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-300 font-semibold">Welcome, {auth.username}</span>
          <button onClick={onLogout} className="bg-campaign-red px-4 py-2 text-sm font-black hover:bg-campaign-red-hover transition-colors" data-testid="admin-logout">
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="p-6">
        {/* Tabs */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => { setActiveTab('events'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'events' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-events-tab"
          >
            Events
          </button>
          <button
            onClick={() => { setActiveTab('news'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'news' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-news-tab"
          >
            News
          </button>
          <button
            onClick={() => { setActiveTab('endorsements'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'endorsements' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-endorsements-tab"
          >
            Endorsements
          </button>
          <button
            onClick={() => { setActiveTab('gallery'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'gallery' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-gallery-tab"
          >
            <Image size={18} /> Gallery ({gallery.length})
          </button>
          <button
            onClick={() => { setActiveTab('issues'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'issues' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-issues-tab"
          >
            <Shield size={18} /> Issues ({issues.length})
          </button>
          <button
            onClick={() => { setActiveTab('subscribers'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'subscribers' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-subscribers-tab"
          >
            <Users size={18} /> Subscribers ({subscribers.length})
          </button>
        </div>

        {/* Add Button (not for subscribers) */}
        {activeTab !== 'subscribers' && (
          <div className="mb-6">
            <button
              onClick={() => { setShowForm(true); setEditItem(null); }}
              className="bg-campaign-red text-white px-6 py-3 font-semibold uppercase hover:bg-campaign-red-hover transition-colors"
              data-testid="admin-add-btn"
            >
              Add {activeTab === 'events' ? 'Event' : activeTab === 'news' ? 'News' : activeTab === 'gallery' ? 'Photo' : activeTab === 'issues' ? 'Issue' : 'Endorsement'}
            </button>
          </div>
        )}

        {/* Export Button for subscribers */}
        {activeTab === 'subscribers' && subscribers.length > 0 && (
          <div className="mb-6">
            <button
              onClick={exportSubscribers}
              className="bg-green-600 text-white px-6 py-3 font-semibold uppercase hover:bg-green-700 transition-colors flex items-center gap-2"
              data-testid="admin-export-btn"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-sm shadow mb-6" data-testid="admin-form">
            {activeTab === 'events' ? (
              <EventForm 
                event={editItem} 
                onSave={(data) => handleSave('events', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }} 
              />
            ) : activeTab === 'news' ? (
              <NewsForm 
                article={editItem} 
                onSave={(data) => handleSave('news', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            ) : activeTab === 'gallery' ? (
              <GalleryForm 
                photo={editItem} 
                onSave={(data) => handleSave('gallery', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
                onBulkSave={async (photos) => {
                  for (const photo of photos) {
                    await axios.post(`${API}/admin/gallery`, photo, authConfig);
                  }
                  fetchData();
                }}
              />
            ) : activeTab === 'issues' ? (
              <IssueForm 
                issue={editItem} 
                onSave={(data) => handleSave('issues', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
              />
            ) : (
              <EndorsementForm 
                endorsement={editItem} 
                onSave={(data) => handleSave('endorsements', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-sm shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : activeTab === 'events' ? (
            <table className="w-full" data-testid="admin-events-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-6 py-4">{event.title}</td>
                    <td className="px-6 py-4">{event.date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(event); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('events', event.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No events found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'news' ? (
            <table className="w-full" data-testid="admin-news-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {news.map((article) => (
                  <tr key={article.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.content_type === 'original' ? 'bg-campaign-red/10 text-campaign-red' : 'bg-blue-100 text-blue-800'}`}>
                        {article.content_type === 'original' ? 'Original' : 'External'}
                      </span>
                      {article.video_url && <span className="ml-1 text-xs">🎬</span>}
                    </td>
                    <td className="px-6 py-4 max-w-xs truncate">{article.title}</td>
                    <td className="px-6 py-4">{article.content_type === 'original' ? 'Campaign' : (article.source || '-')}</td>
                    <td className="px-6 py-4">{article.published_date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {article.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(article); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('news', article.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No news articles found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'endorsements' ? (
            <table className="w-full" data-testid="admin-endorsements-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Logo/Image</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {endorsements.map((endorsement) => (
                  <tr key={endorsement.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.endorsement_type === 'organization' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {endorsement.endorsement_type === 'organization' ? 'Org' : 'Individual'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{endorsement.name}</td>
                    <td className="px-6 py-4">{endorsement.title || '-'}</td>
                    <td className="px-6 py-4">
                      {endorsement.image_url ? (
                        <img src={resolveImageUrl(endorsement.image_url)} alt="" className="w-10 h-10 object-contain rounded" />
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {endorsement.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(endorsement); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('endorsements', endorsement.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {endorsements.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No endorsements found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'gallery' ? (
            <div className="p-6">
              {/* Display Mode Selector */}
              <div className="mb-6 p-4 bg-gray-50 rounded flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-700">Display Order:</span>
                  <select
                    value={galleryDisplayMode}
                    onChange={(e) => updateGalleryDisplayMode(e.target.value)}
                    className="border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                    data-testid="gallery-display-mode"
                  >
                    <option value="manual">Manual (by order number)</option>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="random">Random (shuffle)</option>
                  </select>
                </div>
                <p className="text-sm text-gray-500">
                  {galleryDisplayMode === 'manual' && 'Photos will display in the order you set (edit each photo to change order number)'}
                  {galleryDisplayMode === 'newest' && 'Most recently added photos will show first'}
                  {galleryDisplayMode === 'oldest' && 'Oldest photos will show first'}
                  {galleryDisplayMode === 'random' && 'Photos will be shuffled randomly on each page load'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((photo) => (
                  <div key={photo.id} className="relative group bg-gray-100 rounded overflow-hidden" data-testid={`gallery-admin-${photo.id}`}>
                    <img 
                      src={resolveImageUrl(photo.url)} 
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full aspect-square object-cover"
                    />
                    {galleryDisplayMode === 'manual' && (
                      <div className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded font-semibold">
                        #{photo.order || 0}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      <p className="text-white text-xs text-center line-clamp-2">{photo.caption || 'No caption'}</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => { setEditItem(photo); setShowForm(true); }} 
                          className="bg-white text-black px-3 py-1 text-xs font-semibold rounded hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete('gallery', photo.id)} 
                          className="bg-red-600 text-white px-3 py-1 text-xs font-semibold rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {gallery.length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <Image className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p>No photos in gallery. Click "Add Photo" to get started.</p>
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'issues' ? (
            <table className="w-full" data-testid="admin-issues-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Order</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Icon</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {issues.map((issue) => {
                  const IconComponent = ICON_MAP[issue.icon] || Shield;
                  return (
                    <tr key={issue.id}>
                      <td className="px-6 py-4 font-semibold">{issue.order}</td>
                      <td className="px-6 py-4">
                        <div className="w-10 h-10 rounded bg-black/10 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-black" />
                        </div>
                      </td>
                      <td className="px-6 py-4 font-semibold">{issue.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{issue.description}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${issue.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {issue.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => { setEditItem(issue); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                        <button onClick={() => handleDelete('issues', issue.id)} className="text-red-600 hover:underline">Delete</button>
                      </td>
                    </tr>
                  );
                })}
                {issues.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No issues found. Click "Add Issue" to create platform issues.</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full" data-testid="admin-subscribers-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Preferences</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Subscribed</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td className="px-6 py-4">{subscriber.email}</td>
                    <td className="px-6 py-4">{subscriber.first_name || subscriber.last_name ? `${subscriber.first_name || ''} ${subscriber.last_name || ''}`.trim() : '-'}</td>
                    <td className="px-6 py-4">{subscriber.phone || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {subscriber.wants_email && <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">Email</span>}
                        {subscriber.wants_text && <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Text</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(subscriber.subscribed_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleDelete('subscribers', subscriber.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {subscribers.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No subscribers yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// Event Form
const EventForm = ({ event, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    date: event?.date || 'TBD',
    time: event?.time || '',
    location: event?.location || '',
    rsvp_link: event?.rsvp_link || '#',
    image_url: event?.image_url || '',
    is_active: event?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{event ? 'Edit Event' : 'Add Event'}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Date *</label>
          <input
            type="text"
            value={form.date}
            onChange={(e) => setForm({...form, date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., TBD, January 15, 2026"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Time</label>
          <input
            type="text"
            value={form.time}
            onChange={(e) => setForm({...form, time: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., 6:00 PM"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Location</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => setForm({...form, location: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">RSVP Link</label>
          <input
            type="text"
            value={form.rsvp_link}
            onChange={(e) => setForm({...form, rsvp_link: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="Google Form URL or #"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Image URL</label>
          <input
            type="text"
            value={form.image_url}
            onChange={(e) => setForm({...form, image_url: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          />
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="3"
          required
        />
      </div>
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// News Form
const NewsForm = ({ article, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    title: article?.title || '',
    summary: article?.summary || '',
    content: article?.content || '',
    content_type: article?.content_type || 'external',
    source: article?.source || '',
    link: article?.link || '',
    image_url: article?.image_url || '',
    video_url: article?.video_url || '',
    published_date: article?.published_date || new Date().toISOString().split('T')[0],
    is_active: article?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  // Extract YouTube video ID for preview
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(form.video_url);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{article ? 'Edit News' : 'Add News'}</h3>
      
      {/* Content Type Selector */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <label className="block text-gray-700 font-semibold mb-3">Post Type</label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="external"
              checked={form.content_type === 'external'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">External Link</span>
            <span className="text-gray-500 text-sm">(Link to news article)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="original"
              checked={form.content_type === 'original'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">Original Post</span>
            <span className="text-gray-500 text-sm">(Your own content/video)</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Published Date *</label>
          <input
            type="date"
            value={form.published_date}
            onChange={(e) => setForm({...form, published_date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        
        {form.content_type === 'external' ? (
          <>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Source *</label>
              <input
                type="text"
                value={form.source}
                onChange={(e) => setForm({...form, source: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                placeholder="e.g., Yonkers Tribune"
                required={form.content_type === 'external'}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Article URL *</label>
              <input
                type="url"
                value={form.link}
                onChange={(e) => setForm({...form, link: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                placeholder="https://..."
                required={form.content_type === 'external'}
              />
            </div>
          </>
        ) : (
          <div className="md:col-span-2">
            <label className="block text-gray-700 font-semibold mb-2">Video URL (YouTube)</label>
            <input
              type="url"
              value={form.video_url}
              onChange={(e) => setForm({...form, video_url: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {videoId && (
              <div className="mt-3">
                <p className="text-sm text-gray-500 mb-2">Preview:</p>
                <div className="aspect-video w-64 bg-black rounded overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video preview"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Featured Image */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label="Featured Image"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">
          {form.content_type === 'external' ? 'Summary *' : 'Summary / Excerpt *'}
        </label>
        <textarea
          value={form.summary}
          onChange={(e) => setForm({...form, summary: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="3"
          required
          placeholder={form.content_type === 'external' ? 'Brief summary of the article...' : 'Short excerpt shown in the news list...'}
        />
      </div>
      
      {/* Full Content for Original Posts */}
      {form.content_type === 'original' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Full Content</label>
          <RichTextEditor
            value={form.content}
            onChange={(content) => setForm({...form, content})}
          />
          <p className="text-sm text-gray-500 mt-1">Use the toolbar above to format your content</p>
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Image Upload Component
const ImageUpload = ({ value, onChange, authConfig, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(resolveImageUrl(value) || '');
  const fileInputRef = React.useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/upload`, formData, {
        ...authConfig,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store just the relative path - resolveImageUrl will add the correct domain
      const uploadedUrl = res.data.url;
      onChange(uploadedUrl);
      setPreview(resolveImageUrl(uploadedUrl));
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload image. Please try again.');
      setPreview(value || '');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange(url);
    setPreview(url);
  };

  const handleRemove = () => {
    onChange('');
    setPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-gray-700 font-semibold mb-2">{label}</label>
      
      {/* Preview */}
      {preview && (
        <div className="mb-3 relative inline-block">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-24 h-24 object-contain border rounded bg-gray-50"
          />
          <button 
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
          >
            ×
          </button>
        </div>
      )}
      
      <div className="flex gap-2 items-center">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-${label.replace(/\s/g, '-')}`}
        />
        <label 
          htmlFor={`file-upload-${label.replace(/\s/g, '-')}`}
          className={`cursor-pointer bg-gray-100 border border-gray-300 px-4 py-2 rounded text-sm font-semibold hover:bg-gray-200 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </label>
        <span className="text-gray-400 text-sm">or</span>
        <input
          type="text"
          placeholder="Paste image URL"
          value={preview.startsWith('data:') ? '' : (preview || '')}
          onChange={handleUrlChange}
          className="flex-1 border border-gray-300 px-3 py-2 rounded text-sm focus:outline-none focus:border-black"
        />
      </div>
    </div>
  );
};

// Gallery Form (with bulk upload support for new photos)
const GalleryForm = ({ photo, onSave, onCancel, authConfig, onBulkSave }) => {
  const [form, setForm] = useState({
    url: photo?.url || '',
    caption: photo?.caption || '',
    order: photo?.order || 0,
    is_active: photo?.is_active ?? true
  });
  
  // Bulk upload state
  const [bulkMode, setBulkMode] = useState(!photo); // Default to bulk mode for new photos
  const [bulkFiles, setBulkFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const dropZoneRef = React.useRef(null);
  const bulkInputRef = React.useRef(null);
  
  // Cropping state
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperIndex, setCropperIndex] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.url) {
      alert('Please upload or enter an image URL');
      return;
    }
    onSave(form);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Open cropper for a specific image
  const openCropper = (index) => {
    const file = bulkFiles[index];
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImage(reader.result);
      setCropperIndex(index);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle crop complete
  const handleCropComplete = (croppedBlob) => {
    // Create a new file from the cropped blob
    const croppedFile = new File([croppedBlob], bulkFiles[cropperIndex].name, { type: 'image/jpeg' });
    
    // Replace the file in bulkFiles
    setBulkFiles(prev => {
      const newFiles = [...prev];
      newFiles[cropperIndex] = croppedFile;
      return newFiles;
    });
    
    // Close cropper
    setCropperImage(null);
    setCropperIndex(null);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress({ current: 0, total: bulkFiles.length });
    
    const uploadedPhotos = [];
    
    for (let i = 0; i < bulkFiles.length; i++) {
      const file = bulkFiles[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/admin/upload`, formData, {
          ...authConfig,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        // Store just the relative path
        const uploadedUrl = res.data.url;
        uploadedPhotos.push({
          url: uploadedUrl,
          caption: '',
          order: i,
          is_active: true
        });
        
        setUploadProgress({ current: i + 1, total: bulkFiles.length });
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }
    
    setUploading(false);
    
    if (uploadedPhotos.length > 0 && onBulkSave) {
      await onBulkSave(uploadedPhotos);
    }
    
    setBulkFiles([]);
    onCancel();
  };

  // If editing existing photo, show single edit form
  if (photo) {
    return (
      <form onSubmit={handleSubmit}>
        <h3 className="font-heading text-xl text-black uppercase mb-4">Edit Photo</h3>
        
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Current Photo</label>
          <img 
            src={resolveImageUrl(form.url)} 
            alt="Current" 
            className="w-32 h-32 object-cover rounded border"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Caption</label>
            <input
              type="text"
              value={form.caption}
              onChange={(e) => setForm({...form, caption: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="Describe this photo..."
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              min="0"
            />
          </div>
        </div>
        
        <div className="mb-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
        
        <div className="flex gap-4">
          <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
            Save
          </button>
          <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    );
  }

  // Bulk upload form for new photos
  return (
    <div>
      <h3 className="font-heading text-xl text-black uppercase mb-4">Add Photos (up to 30)</h3>
      
      {/* Drop zone */}
      <div
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-black transition-colors cursor-pointer mb-4"
        onClick={() => bulkInputRef.current?.click()}
      >
        <input
          type="file"
          ref={bulkInputRef}
          accept="image/*"
          multiple
          onChange={handleBulkFileSelect}
          className="hidden"
        />
        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-semibold mb-1">Drop photos here or click to select</p>
        <p className="text-gray-400 text-sm">Upload up to 30 photos at once. Add captions later.</p>
      </div>
      
      {/* Preview of selected files */}
      {bulkFiles.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-700 font-semibold">{bulkFiles.length} photo(s) selected</p>
            <p className="text-sm text-gray-500">Click <Crop size={14} className="inline" /> to crop any photo</p>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-64 overflow-y-auto p-1">
            {bulkFiles.map((file, index) => (
              <div key={`${file.name}-${file.size}-${file.lastModified}`} className="relative group">
                <img 
                  src={URL.createObjectURL(file)} 
                  alt={`Preview ${index + 1}`}
                  className="w-full aspect-square object-cover rounded border-2 border-transparent group-hover:border-black transition-colors"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => openCropper(index)}
                    className="bg-white text-black p-1.5 rounded hover:bg-gray-100"
                    title="Crop image"
                  >
                    <Crop size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeBulkFile(index)}
                    className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Image Cropper Modal */}
      {cropperImage && (
        <ImageCropperModal
          imageSrc={cropperImage}
          onCropComplete={handleCropComplete}
          onCancel={() => { setCropperImage(null); setCropperIndex(null); }}
          aspectRatio={1}
        />
      )}
      
      {/* Upload progress */}
      {uploading && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full"></div>
            <span className="text-gray-600">Uploading {uploadProgress.current} of {uploadProgress.total}...</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-black h-2 rounded-full transition-all duration-300"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <button 
          type="button"
          onClick={handleBulkUpload}
          disabled={bulkFiles.length === 0 || uploading}
          className="bg-campaign-red text-white px-6 py-2 font-semibold hover:bg-campaign-red-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : `Upload ${bulkFiles.length} Photo${bulkFiles.length !== 1 ? 's' : ''}`}
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// Endorsement Form
const EndorsementForm = ({ endorsement, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    name: endorsement?.name || '',
    title: endorsement?.title || '',
    organization: endorsement?.organization || '',
    quote: endorsement?.quote || '',
    image_url: endorsement?.image_url || '',
    endorsement_type: endorsement?.endorsement_type || 'individual',
    is_active: endorsement?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{endorsement ? 'Edit Endorsement' : 'Add Endorsement'}</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Endorsement Type *</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="organization"
              checked={form.endorsement_type === 'organization'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><Building2 size={16} /> Organization</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="individual"
              checked={form.endorsement_type === 'individual'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><User size={16} /> Individual</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Organization Name *' : 'Name *'}
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Yonkers Republican Committee' : 'e.g., Mike Spano'}
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Tagline/Description' : 'Title'}
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Serving Yonkers since 1920' : 'e.g., Mayor of Yonkers'}
          />
        </div>
        {form.endorsement_type === 'individual' && (
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Organization</label>
            <input
              type="text"
              value={form.organization}
              onChange={(e) => setForm({...form, organization: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="e.g., Yonkers City Council"
            />
          </div>
        )}
      </div>
      
      {/* Image Upload */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label={form.endorsement_type === 'organization' ? 'Organization Logo' : 'Photo'}
        />
      </div>
      
      {form.endorsement_type === 'individual' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Quote</label>
          <textarea
            value={form.quote}
            onChange={(e) => setForm({...form, quote: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            rows="3"
            placeholder="Optional endorsement quote..."
          />
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Issue Form
const IssueForm = ({ issue, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: issue?.title || '',
    icon: issue?.icon || 'Shield',
    description: issue?.description || '',
    details: issue?.details || [''],
    order: issue?.order || 0,
    is_active: issue?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Filter out empty details
    const cleanedDetails = form.details.filter(d => d.trim() !== '');
    onSave({ ...form, details: cleanedDetails });
  };

  const addDetail = () => {
    setForm({ ...form, details: [...form.details, ''] });
  };

  const removeDetail = (index) => {
    setForm({ ...form, details: form.details.filter((_, i) => i !== index) });
  };

  const updateDetail = (index, value) => {
    const newDetails = [...form.details];
    newDetails[index] = value;
    setForm({ ...form, details: newDetails });
  };

  const iconOptions = Object.keys(ICON_MAP);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{issue ? 'Edit Issue' : 'Add Issue'}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2">
          <label className="block text-gray-700 font-semibold mb-2">Issue Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            required
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., Fix Public Safety"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Icon</label>
          <select
            value={form.icon}
            onChange={(e) => setForm({...form, icon: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          required
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="2"
          placeholder="Brief description shown on the card..."
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Key Points</label>
        {form.details.map((detail, index) => (
          <div key={`detail-${index}-${detail.slice(0, 10)}`} className="flex gap-2 mb-2">
            <input
              type="text"
              value={detail}
              onChange={(e) => updateDetail(index, e.target.value)}
              className="flex-1 border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder={`Point ${index + 1}...`}
            />
            <button
              type="button"
              onClick={() => removeDetail(index)}
              className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addDetail}
          className="text-black text-sm font-semibold hover:underline"
        >
          + Add another point
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            min="0"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Admin Page Wrapper
const AdminPage = () => {
  const [auth, setAuth] = useState(null);

  const handleLogin = (credentials) => {
    setAuth(credentials);
    // Use sessionStorage instead of localStorage for security
    sessionStorage.setItem('adminAuth', JSON.stringify(credentials));
  };

  const handleLogout = () => {
    setAuth(null);
    sessionStorage.removeItem('adminAuth');
  };

  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth) {
      setAuth(JSON.parse(savedAuth));
    }
  }, []);

  if (!auth) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard auth={auth} onLogout={handleLogout} />;
};

// Main App Layout
const MainLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
};

// Donate Redirect Component
const DonateRedirect = () => {
  useEffect(() => {
    window.location.href = 'https://secure.winred.com/neider-for-assembly/donate';
  }, []);
  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center text-white">
        <p className="text-xl">Redirecting to donation page...</p>
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  usePageTitle("Privacy Policy");
  return (
    <div className="pt-20" data-testid="privacy-page">
      <section className="py-12 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase">Privacy Policy</h1>
        </div>
      </section>
      
      <section className="py-16">
        <div className="container-campaign">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="text-gray-600 mb-8">Last Updated: March 2026</p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Introduction</h2>
            <p className="text-gray-700 mb-6">
              Neider for Assembly ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or sign up for communications from our campaign.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Information We Collect</h2>
            <p className="text-gray-700 mb-4">We may collect information about you in a variety of ways, including:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li><strong>Personal Data:</strong> Name, email address, phone number, mailing address, and other information you voluntarily provide when signing up for updates or making a donation.</li>
              <li><strong>Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, and pages visited.</li>
            </ul>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Send you campaign updates, event information, and volunteer opportunities</li>
              <li>Process donations and send receipts</li>
              <li>Respond to your inquiries and requests</li>
              <li>Improve our website and campaign operations</li>
              <li>Comply with legal obligations</li>
            </ul>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Text Messaging Consent and Data</h2>
            <div className="bg-gray-100 p-6 rounded-sm mb-6">
              <p className="text-gray-800 font-semibold">
                We will not share text messaging originator opt-in data and consent, provided that the foregoing does not apply to sharing (1) with vendors, consultants and other service providers who need access to such information to carry out work on our behalf (and who will not use such information for their own purposes); (2) if we believe disclosure is required by any applicable law, rule, or regulation or to comply with law enforcement or legal process; and (3) if the user consents to our sharing of such information.
              </p>
            </div>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Sharing Your Information</h2>
            <p className="text-gray-700 mb-6">
              We do not sell your personal information. We may share your information with trusted service providers who assist us in operating our website and conducting our campaign, provided they agree to keep your information confidential.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Opt-out of receiving text messages by replying STOP</li>
              <li>Opt-out of receiving emails by clicking the unsubscribe link</li>
              <li>Request access to or deletion of your personal information</li>
            </ul>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-6">
              If you have questions about this Privacy Policy, please contact us at:<br />
              <a href="mailto:contact@neiderforassembly.com" className="text-campaign-red hover:underline">contact@neiderforassembly.com</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// Terms of Service Page
const TermsOfServicePage = () => {
  usePageTitle("Terms of Service");
  return (
    <div className="pt-20" data-testid="terms-page">
      <section className="py-12 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase">Terms of Service</h1>
        </div>
      </section>
      
      <section className="py-16">
        <div className="container-campaign">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="text-gray-600 mb-8">Last Updated: March 2026</p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Agreement to Terms</h2>
            <p className="text-gray-700 mb-6">
              By accessing or using the Neider for Assembly website, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Mobile Terms of Service</h2>
            <p className="text-gray-700 mb-4">
              By providing your phone number and opting in, you consent to receive recurring automated promotional and personalized marketing text messages (e.g., SMS and MMS) from us.
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li><strong>Messages may include</strong> campaign updates, event information, volunteer opportunities, and requests for donation.</li>
              <li>Consent to receive text messages is program-specific and does not constitute consent to receive calls or messages from other programs or third parties.</li>
              <li><strong>Consent</strong> to receive automated marketing text messages is not a condition of any purchase or donation.</li>
              <li><strong>You may cancel</strong> the SMS service at any time. Text "STOP" to the number from which you received the message.</li>
              <li><strong>For assistance,</strong> reply "HELP" to any message or contact contact@neiderforassembly.com.</li>
              <li><strong>Message frequency</strong> varies based on activity and your level of engagement. Standard message and data rates may apply.</li>
              <li><strong>Mobile carriers,</strong> including T-Mobile, are not liable for delayed or undelivered messages.</li>
              <li><strong>Phone number is optional</strong> and not required for other services on this website.</li>
            </ul>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Intellectual Property</h2>
            <p className="text-gray-700 mb-6">
              All content on this website, including text, graphics, logos, and images, is the property of Neider for Assembly and is protected by applicable intellectual property laws.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              This website is provided "as is" without warranties of any kind, either express or implied. We do not warrant that the website will be uninterrupted or error-free.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Limitation of Liability</h2>
            <p className="text-gray-700 mb-6">
              Neider for Assembly shall not be liable for any damages arising from the use or inability to use this website.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Privacy - Text Messaging Data</h2>
            <div className="bg-gray-100 p-6 rounded-sm mb-6 border-l-4 border-campaign-red">
              <p className="text-gray-800 font-semibold">
                We will not share text messaging originator opt-in data and consent, provided that the foregoing does not apply to sharing (1) with vendors, consultants and other service providers who need access to such information to carry out work on our behalf (and who will not use such information for their own purposes); (2) if we believe disclosure is required by any applicable law, rule, or regulation or to comply with law enforcement or legal process; and (3) if the user consents to our sharing of such information.
              </p>
            </div>
            <p className="text-gray-700 mb-6">
              For our full Privacy Policy, please visit our <Link to="/privacy" className="text-campaign-red hover:underline">Privacy Policy page</Link>.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Changes to Terms</h2>
            <p className="text-gray-700 mb-6">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the website.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mb-4">Contact Us</h2>
            <div className="text-gray-700 mb-6">
              <p className="font-semibold">Neider for Assembly</p>
              <p>P.O. Box 1033 - Yonkers, NY 10704</p>
              <p><a href="mailto:contact@neiderforassembly.com" className="text-campaign-red hover:underline">contact@neiderforassembly.com</a></p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<MainLayout><Home /></MainLayout>} />
          <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
          <Route path="/events" element={<MainLayout><EventsPage /></MainLayout>} />
          <Route path="/news" element={<MainLayout><NewsPage /></MainLayout>} />
          <Route path="/privacy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
          <Route path="/terms" element={<MainLayout><TermsOfServicePage /></MainLayout>} />
          <Route path="/donate" element={<DonateRedirect />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
